import React, { useState, useCallback, useEffect, useRef } from 'react';
import { SellerLayout } from '../SellerLayout';
import { HandoffMarker } from '../../../components/HandoffMarker/HandoffMarker';
import {
  PageHeading,
  CardBalance,
  Pills,
  Button,
  Text,
  Heading,
  Tooltip,
  Drawer,
  Input,
  Alert,
  Loading,
  Icon,
  Avatar,
  Section,
  Modal,
  useToast,
} from 'barte-design-system';
import {
  ArrowRightIcon,
  CheckIcon,
  LockIcon,
  CaretRightIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import './DashboardSaldosSemBanking.css';

// ─── Tooltip cast (children omitido no tipo público) ─────────────────────────

const TooltipAny = Tooltip as React.ForwardRefExoticComponent<any>;

// ─── Ícones dos cards (chevron e lock) ───────────────────────────────────────

const ChevronIcon: React.ElementType = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    <Icon icon={CaretRightIcon} size={16} />
  </span>
);

const BolsaoLockIcon: React.ElementType = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    <Icon icon={LockIcon} size={16} />
  </span>
);


// ─── Conteúdo dos drawers de info (substitui os tooltips dos cards) ───────────

type InfoCardKey = 'processamento' | 'futuros';

type PillStateLite = 'accent' | 'warning' | 'success' | 'info' | 'default';

type InfoRow = {
  label: string;
  value: string;
  pillState: PillStateLite;
};

type Bullet = { term: string; description: string };

type CardInfo = {
  title: string;
  intro: string;
  bullets?: Bullet[];
  rows: InfoRow[];
};

const CARD_INFO: Record<InfoCardKey, CardInfo> = {
  processamento: {
    title: 'Saque em liquidação',
    intro: 'Valores em processo de liquidação para sua conta corrente.',
    bullets: [
      { term: 'Liquida hoje', description: 'Antecipações e saques programados ou solicitados manualmente.' },
      { term: 'Liquida até próximo dia útil', description: 'Vendas em cartão que entram com liquidação automática.' },
    ],
    rows: [
      { label: 'Liquida hoje', value: 'R$ 170.000,00', pillState: 'warning' },
      { label: 'Liquida até próximo dia útil', value: 'R$ 180.000,00', pillState: 'warning' },
    ],
  },
  futuros: {
    title: 'Lançamentos futuros',
    intro:
      'Vendas com data prevista de liberação que ainda não entraram na fila.\n\nEste saldo pode ser antecipado para sacar antes do prazo de liquidação natural.',
    rows: [
      { label: 'Pode antecipar', value: 'R$ 120.000,00', pillState: 'accent' },
    ],
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

type SaqueStep = 'empty' | 'filled' | 'review' | 'processing' | 'success';
type PeriodKey = 'week' | 'month' | 'jan';

// ─── Mock data ────────────────────────────────────────────────────────────────

const DISPONIVEL = 40000;
const PROCESSAMENTO = 350000;
const FUTURO_A_LIBERAR = 120000;
const GRAVAMES = 30000;
const BOLSAO = 2450;
const SALDO_TOTAL = DISPONIVEL + PROCESSAMENTO + FUTURO_A_LIBERAR + GRAVAMES + BOLSAO;

const CHART_DATA = [
  { label: '01', received: 0, overdue: 0 },
  { label: '03', received: 0, overdue: 0 },
  { label: '05', received: 0, overdue: 0 },
  { label: '07', received: 0, overdue: 0 },
  { label: '08', received: 45, overdue: 40, isToday: true },
  { label: '11', received: 0, overdue: 0 },
  { label: '13', received: 0, overdue: 0 },
  { label: '15', received: 0, overdue: 0 },
  { label: '17', received: 0, overdue: 0 },
  { label: '18', received: 0, overdue: 0 },
  { label: '21', received: 0, overdue: 0 },
  { label: '23', received: 0, overdue: 0 },
  { label: '25', received: 0, overdue: 0 },
  { label: '27', received: 0, overdue: 0 },
  { label: '28', received: 0, overdue: 0 },
  { label: '31', received: 0, overdue: 0 },
];

const MAX_CHART_VALUE = Math.max(...CHART_DATA.map((d) => d.received + d.overdue), 1);

const RECEBIVEIS_MOCK = [
  {
    id: '1',
    initials: 'VG',
    name: 'Vinicius Gonçalves...',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 1,00',
    status: 'Em atraso' as const,
    statusState: 'error' as const,
  },
  {
    id: '2',
    initials: 'VG',
    name: 'Vinicius Gonçalves...',
    dueLabel: 'venc. 06/01',
    amount: 'R$ 1,00',
    status: 'Paga - 06/01' as const,
    statusState: 'success' as const,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSaqueDrawerTitle(step: SaqueStep): string {
  if (step === 'processing' || step === 'success') return 'Finalizando saque';
  if (step === 'review') return 'Revisar saque';
  return 'Solicitar saque';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DashboardSaldosSemBanking() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saqueStep, setSaqueStep] = useState<SaqueStep>('empty');
  const [saqueValue, setSaqueValue] = useState('');
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('month');
  const [infoCard, setInfoCard] = useState<InfoCardKey | null>(null);
  const [abrirContaModalOpen, setAbrirContaModalOpen] = useState(false);

  const openInfoDrawer = useCallback((card: InfoCardKey) => setInfoCard(card), []);
  const closeInfoDrawer = useCallback(() => setInfoCard(null), []);

  const handleCardKeyDown = useCallback(
    (card: InfoCardKey) => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setInfoCard(card);
      }
    },
    [],
  );

  const toast = useToast();

  // Ref + auto-focus do input de valor quando o drawer abre / volta de review
  const valueInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (drawerOpen && (saqueStep === 'empty' || saqueStep === 'filled')) {
      const t = setTimeout(() => valueInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [drawerOpen, saqueStep]);

  // ── Drawer handlers ──────────────────────────────────────────────────────

  const handleOpenDrawer = useCallback(() => {
    setSaqueStep('empty');
    setSaqueValue('');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => {
      setSaqueStep('empty');
      setSaqueValue('');
    }, 300);
  }, []);

  const DISPONIVEL_TOTAL = DISPONIVEL + FUTURO_A_LIBERAR;

  const formatCurrency = (raw: string) => {
    const digits = raw.replaceAll(/\D/g, '');
    if (!digits) return '';
    const num = Number.parseInt(digits, 10) / 100;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseValue = (formatted: string) => {
    if (!formatted) return 0;
    return Number.parseFloat(formatted.replaceAll('.', '').replace(',', '.')) || 0;
  };

  const calcSaque = (formatted: string) => {
    const val = parseValue(formatted);
    const excede = val > DISPONIVEL_TOTAL;
    const taxa = val > DISPONIVEL ? (val - DISPONIVEL) * 0.05 : 0;
    const recebe = val - taxa;
    return { val, excede, taxa, recebe };
  };

  const handleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const formatted = formatCurrency(e.target.value);
      setSaqueValue(formatted);
      setSaqueStep(formatted ? 'filled' : 'empty');
    },
    [],
  );

  const handleGoToReview = useCallback(() => {
    setSaqueStep('review');
  }, []);

  const handleConfirm = useCallback(() => {
    const val = parseValue(saqueValue);
    const taxa = val > DISPONIVEL ? (val - DISPONIVEL) * 0.05 : 0;
    const recebe = val - taxa;

    // 1. loading
    setSaqueStep('processing');

    // 2. check animado após 1.5s
    setTimeout(() => {
      setSaqueStep('success');

      // 3. fecha o drawer + dispara o toast após 900ms
      setTimeout(() => {
        setDrawerOpen(false);
        toast.success({
          title: 'Saque solicitado com sucesso',
          description: `Saldo de R$ ${recebe.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} já está em processo de liquidação para sua conta corrente.`,
        });

        // 4. reset do step após a animação do drawer fechar
        setTimeout(() => {
          setSaqueStep('empty');
          setSaqueValue('');
        }, 300);
      }, 900);
    }, 1500);
  }, [saqueValue, toast]);

  const handleBackToFilled = useCallback(() => {
    setSaqueStep('filled');
  }, []);

  // ── Drawer footer ─────────────────────────────────────────────────────────

  function getDrawerFooter() {
    // review / processing / success → mantém o par "Solicitar saque" + "Voltar"
    if (
      saqueStep === 'review' ||
      saqueStep === 'processing' ||
      saqueStep === 'success'
    ) {
      const locked = saqueStep === 'processing' || saqueStep === 'success';
      return {
        primaryButton: {
          children: 'Solicitar saque',
          iconRight: <Icon icon={ArrowRightIcon} size={16} />,
          onClick: handleConfirm,
          disabled: locked,
        },
        secondaryButton: {
          children: 'Voltar',
          onClick: handleBackToFilled,
          disabled: locked,
        },
      };
    }

    // empty / filled → "Revisar" + "Cancelar"
    return {
      primaryButton: {
        children: 'Revisar',
        iconRight: <Icon icon={ArrowRightIcon} size={16} />,
        onClick: handleGoToReview,
        disabled: saqueStep === 'empty' || calcSaque(saqueValue).excede,
      },
      secondaryButton: {
        children: 'Cancelar',
        onClick: handleCloseDrawer,
      },
    };
  }

  // ── Drawer content ────────────────────────────────────────────────────────

  function renderReviewContent() {
    const { taxa, recebe } = calcSaque(saqueValue);
    const fmt = (n: number) =>
      n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
      <div className="drawer-saque">
        <Text variant="body-sm" style={{ color: 'var(--content-secondary)' }}>
          Confirme o valor solicitado:
        </Text>

        <Section title="">
          <Section.Row
            label="Destino"
            content={<Text variant="body-sm">Conta Itaú</Text>}
          />
          <Section.Row
            label="Total solicitado"
            content={<Text variant="body-sm">R$ {saqueValue}</Text>}
          />
        </Section>

        <Section title="">
          <Section.Row
            label="Valor de saque"
            content={
              <Text variant="body-sm">
                R$ {fmt(Math.min(calcSaque(saqueValue).val, DISPONIVEL))}
              </Text>
            }
          />
          <Section.Row
            label="Antecipando"
            content={
              <Text variant="body-sm">
                R$ {fmt(Math.max(0, calcSaque(saqueValue).val - DISPONIVEL))}
              </Text>
            }
          />
          <Section.Row
            label="Taxa de antecipação (4,9%)"
            content={
              <Text variant="body-sm" style={{ color: 'var(--accent-red)' }}>
                - R$ {fmt(taxa)}
              </Text>
            }
          />
          <Section.Row
            className="drawer-review-total-row"
            label="Você recebe"
            content={<Text variant="body-sm">R$ {fmt(recebe)}</Text>}
          />
        </Section>

        <Alert
          variant="info"
          title=""
          description="O valor solicitado vai aparecer em 'Saque em liquidação' e será transferido para a conta bancária cadastrada."
          showCloseButton={false}
        />
      </div>
    );
  }

  function renderDrawerContent() {
    if (saqueStep === 'processing') {
      return (
        <div className="drawer-loading">
          <Loading type="page" description="Processando solicitação..." />
        </div>
      );
    }

    if (saqueStep === 'success') {
      return (
        <div className="drawer-loading">
          <div className="drawer-success-icon drawer-success-icon--animated">
            <Icon icon={CheckIcon} size={32} color="white" weight="bold" />
          </div>
        </div>
      );
    }

    if (saqueStep === 'review') {
      return renderReviewContent();
    }

    const { excede } = calcSaque(saqueValue);

    const fmt = (n: number) =>
      n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const setQuickValue = (val: number) => {
      const formatted = val.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      setSaqueValue(formatted);
      setSaqueStep('filled');
    };

    const valorAtual = parseValue(saqueValue);
    const quickPill1Selected = valorAtual === DISPONIVEL && valorAtual > 0;
    const quickPill2Selected = valorAtual === DISPONIVEL_TOTAL && valorAtual > 0;

    return (
      <div className="drawer-saque">
        {/* Intro acima da Section */}
        <Text variant="body-sm" style={{ color: 'var(--content-secondary)' }}>
          Valores que você pode sacar tanto de saldo atual como antecipar
          recebíveis futuros.
        </Text>

        {/* Breakdown como Section padrão, com pills nos valores */}
        <Section title="">
          <Section.Row
            label="Disponível para saque"
            content={
              <Pills
                label={`R$ ${fmt(DISPONIVEL)}`}
                state="success"
                variant="light"
                size="sm"
              />
            }
          />
          <Section.Row
            label="Disponível para antecipação"
            content={
              <Pills
                label={`R$ ${fmt(FUTURO_A_LIBERAR)}`}
                state="accent"
                variant="light"
                size="sm"
              />
            }
          />
          <Section.Row
            className="drawer-review-total-row"
            label="Total disponível"
            content={
              <Pills
                label={`R$ ${fmt(DISPONIVEL_TOTAL)}`}
                state="default"
                variant="light"
                size="sm"
              />
            }
          />
        </Section>

        {/* Card com input + chips de atalho */}
        <div className="drawer-input-card">
        <Input
          ref={valueInputRef as React.Ref<HTMLInputElement>}
          label="Qual o valor você quer sacar?"
          placeholder="R$ 0,00"
          value={saqueValue ? `R$ ${saqueValue}` : ''}
          onChange={handleValueChange}
          error={excede ? 'Valor superior ao disponível para saque.' : undefined}
        />

        {/* Chips de atalho para preencher o valor (pill-shaped, sem feedback de botão) */}
        <div className="drawer-quick-pills">
          <span
            className={`drawer-quick-chip${
              quickPill1Selected ? ' drawer-quick-chip--active' : ''
            }`}
            role="button"
            tabIndex={0}
            onClick={() => setQuickValue(DISPONIVEL)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setQuickValue(DISPONIVEL);
              }
            }}
          >
            Sacar {fmt(DISPONIVEL)}
          </span>
          <span
            className={`drawer-quick-chip${
              quickPill2Selected ? ' drawer-quick-chip--active' : ''
            }`}
            role="button"
            tabIndex={0}
            onClick={() => setQuickValue(DISPONIVEL_TOTAL)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setQuickValue(DISPONIVEL_TOTAL);
              }
            }}
          >
            Sacar {fmt(DISPONIVEL_TOTAL)}
          </span>
        </div>
        </div>

      </div>
    );
  }

  // ── Helpers para cards de saldo ──────────────────────────────────────────

  const formatBRL = (n: number) =>
    n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <SellerLayout activeItem="sem-banking">
      <main className="content">
          <div className="content-inner">
            <PageHeading title="Olá, Madu" />

            <div className="dashboard-sem-banking">
              {/* ── Top: hero (saldo atual + disponível) + banking ──────── */}
              <div className="dashboard-top">
                <div className="hero-card">
                  {/* Card 1 — Saldo atual */}
                  <div className="hero-stat">
                    <div className="hero-stat-header">
                      <Text variant="body-xs" style={{ color: 'var(--content-tertiary)' }}>
                        Saldo atual
                      </Text>
                    </div>
                    <div className="hero-stat-bottom">
                      <Pills
                        label="Total de vendas"
                        state="default"
                        variant="light"
                        size="sm"
                        icon={CurrencyDollarIcon}
                      />
                      <Heading variant="h3">R${formatBRL(SALDO_TOTAL)}</Heading>
                      <div className="hero-bar hero-bar--4">
                      <TooltipAny
                        title="Liberado para saque"
                        label={`R$ ${formatBRL(DISPONIVEL)}`}
                        placement="top"
                        style={{ flex: DISPONIVEL, display: 'flex' }}
                      >
                        <div
                          className="hero-bar-segment"
                          style={{ background: 'var(--accent-green)' }}
                        />
                      </TooltipAny>
                      <TooltipAny
                        title="Até próximo dia útil"
                        label={`R$ ${formatBRL(PROCESSAMENTO)}`}
                        placement="top"
                        style={{ flex: PROCESSAMENTO, display: 'flex' }}
                      >
                        <div
                          className="hero-bar-segment"
                          style={{ background: 'var(--accent-orange)' }}
                        />
                      </TooltipAny>
                      <TooltipAny
                        title="Pode antecipar"
                        label={`R$ ${formatBRL(FUTURO_A_LIBERAR)}`}
                        placement="top"
                        style={{ flex: FUTURO_A_LIBERAR, display: 'flex' }}
                      >
                        <div
                          className="hero-bar-segment"
                          style={{ background: 'var(--accent-purple)' }}
                        />
                      </TooltipAny>
                      <TooltipAny
                        title="Gravames"
                        label={`R$ ${formatBRL(GRAVAMES)}`}
                        placement="top"
                        style={{ flex: GRAVAMES, display: 'flex' }}
                      >
                        <div
                          className="hero-bar-segment"
                          style={{ background: 'var(--accent-blue)' }}
                        />
                      </TooltipAny>
                      <TooltipAny
                        title="Bolsão"
                        label={`R$ ${formatBRL(BOLSAO)}`}
                        placement="top"
                        style={{ flex: BOLSAO, display: 'flex' }}
                      >
                        <div
                          className="hero-bar-segment"
                          style={{ background: 'var(--content-tertiary)' }}
                        />
                      </TooltipAny>
                    </div>
                    </div>
                  </div>

                  <div className="hero-divider" />

                  {/* Card 2 — Disponível para saque */}
                  <div className="hero-stat">
                    <div className="hero-stat-header">
                      <Text variant="body-xs" style={{ color: 'var(--content-tertiary)' }}>
                        Disponível para saque
                      </Text>
                      <Button
                        variant="fill"
                        size="sm"
                        round="full"
                        onClick={handleOpenDrawer}
                      >
                        Solicitar saque
                      </Button>
                    </div>
                    <div className="hero-stat-bottom">
                      <Pills
                        label="Pode sacar ou antecipar"
                        state="default"
                        variant="light"
                        size="sm"
                        icon={CheckCircleIcon}
                      />
                      <Heading variant="h3">R${formatBRL(DISPONIVEL + FUTURO_A_LIBERAR)}</Heading>
                      <div className="hero-bar hero-bar--2">
                      <TooltipAny
                        title="Liberado para saque"
                        label={`R$ ${formatBRL(DISPONIVEL)}`}
                        placement="top"
                        style={{ flex: DISPONIVEL, display: 'flex' }}
                      >
                        <div
                          className="hero-bar-segment"
                          style={{ background: 'var(--accent-green)' }}
                        />
                      </TooltipAny>
                      <TooltipAny
                        title="Pode antecipar"
                        label={`R$ ${formatBRL(FUTURO_A_LIBERAR)}`}
                        placement="top"
                        style={{ flex: FUTURO_A_LIBERAR, display: 'flex' }}
                      >
                        <div
                          className="hero-bar-segment"
                          style={{ background: 'var(--accent-purple)' }}
                        />
                      </TooltipAny>
                    </div>
                    </div>
                  </div>
                </div>

              {/* Banking — card todo clicável (abre modal "Conheça a Conta Corrente Barte") */}
              <div className="dashboard-banking banking-sem-banking banking-sem-banking--with-marker">
                <div
                  className="banking-card-sem-banking banking-card-clickable-sem-banking"
                  role="button"
                  tabIndex={0}
                  onClick={() => setAbrirContaModalOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAbrirContaModalOpen(true);
                    }
                  }}
                >
                  <div className="banking-header-sem-banking">
                    <Text variant="body-xs" style={{ color: 'var(--content-inverse)' }}>
                      Conta Corrente Barte
                    </Text>
                  </div>
                  <div className="banking-bottom-sem-banking">
                    <Text
                      className="banking-tip-text-sem-banking"
                      style={{ color: 'var(--content-inverse)' }}
                    >
                      Conheça a Conta Corrente da Barte e movimente seu saldo
                      com mais praticidade.
                    </Text>
                    <Pills
                      className="banking-pill-white-sem-banking"
                      label="Abra sua conta"
                      state="default"
                      variant="fill"
                      size="base"
                    />
                  </div>
                </div>
                <HandoffMarker
                  top={-8}
                  right={-8}
                  size={16}
                  placement="bottom"
                  note={
                    <ul>
                      <li>
                        Seller ainda <strong>não ativou a Conta Corrente Barte</strong>.
                      </li>
                      <li>
                        Card mantém o fundo preto e o título no topo
                        (<strong>"Conta Corrente Barte"</strong>) — sem
                        chevron e sem valor.
                      </li>
                      <li>
                        Tip em branco, 16px, ocupando a área antes
                        destinada ao valor:{' '}
                        <em>
                          "Conheça a Conta Corrente da Barte e movimente
                          seu saldo com mais praticidade."
                        </em>
                      </li>
                      <li>
                        Pill <strong>"Abra sua conta"</strong> (size base)
                        no rodapé do card.
                      </li>
                      <li>
                        <strong>Card inteiro é clicável</strong> — abre um
                        modal com a copy de apresentação da conta + CTAs
                        "Sim, quero conhecer" e "Não tenho interesse".
                      </li>
                      <li>
                        Clique em <strong>"Sim, quero conhecer"</strong>{' '}
                        fecha o modal e dispara um toast de sucesso
                        agradecendo o interesse e avisando do contato.
                      </li>
                    </ul>
                  }
                />
              </div>
            </div>

            {/* ── 4 cards de saldo (linha full-width) ─────────────────── */}
            <div className="saldo-cards-row">
              <div className="saldo-card-wrapper">
                <CardBalance className="saldo-card saldo-card--disponivel">
                  <CardBalance.Info
                    variant="big"
                    title="Disponível hoje"
                    balance={formatBRL(DISPONIVEL)}
                    showIcon={false}
                    showPills
                    pills={
                      <Pills
                        label="Liberado para saque"
                        state="success"
                        variant="light"
                        size="sm"
                      />
                    }
                  />
                </CardBalance>
              </div>

              <div
                className="saldo-card-clickable"
                onClick={() => openInfoDrawer('futuros')}
                onKeyDown={handleCardKeyDown('futuros')}
                role="button"
                tabIndex={0}
              >
                <CardBalance className="saldo-card saldo-card--futuros">
                  <CardBalance.Info
                    variant="big"
                    title="Lançamentos futuros"
                    balance={formatBRL(FUTURO_A_LIBERAR)}
                    icon={ChevronIcon}
                    showPills
                    pills={
                      <Pills
                        label="Pode antecipar"
                        state="accent"
                        variant="light"
                        size="sm"
                      />
                    }
                  />
                </CardBalance>
              </div>

              <div
                className="saldo-card-clickable"
                onClick={() => openInfoDrawer('processamento')}
                onKeyDown={handleCardKeyDown('processamento')}
                role="button"
                tabIndex={0}
              >
                <CardBalance className="saldo-card saldo-card--processamento">
                  <CardBalance.Info
                    variant="big"
                    title="Saque em liquidação"
                    balance={formatBRL(PROCESSAMENTO)}
                    icon={ChevronIcon}
                    showPills
                    pills={
                      <Pills
                        label="Até próximo dia útil"
                        state="warning"
                        variant="light"
                        size="sm"
                      />
                    }
                    bottomSlot={
                      <div className="card-sublist">
                        <div className="sublist-row">
                          <Text variant="body-xs" style={{ color: 'var(--content-secondary)' }}>
                            Liquida hoje
                          </Text>
                          <Text variant="body-xs" style={{ fontWeight: 600 }}>
                            R$ 170.000,00
                          </Text>
                        </div>
                        <div className="sublist-row">
                          <Text variant="body-xs" style={{ color: 'var(--content-secondary)' }}>
                            Liquida até próximo dia útil
                          </Text>
                          <Text variant="body-xs" style={{ fontWeight: 600 }}>
                            R$ 180.000,00
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </CardBalance>
              </div>

              {/* 4º card: Gravames (não-clicável — saldo travado) */}
              <div className="saldo-card-wrapper">
                <CardBalance className="saldo-card saldo-card--gravames">
                  <CardBalance.Info
                    variant="big"
                    title="Gravames"
                    balance="30.000,00"
                    icon={BolsaoLockIcon}
                    showPills
                    pills={
                      <Pills
                        label="Recebível em garantia"
                        state="info"
                        variant="light"
                        size="sm"
                      />
                    }
                  />
                </CardBalance>
              </div>

              {/* 5º card: Bolsão (não-clicável) */}
              <div className="saldo-card-wrapper">
                <CardBalance className="saldo-card saldo-card--bolsao">
                  <CardBalance.Info
                    variant="big"
                    title="Bolsão"
                    balance="2.450,00"
                    icon={BolsaoLockIcon}
                    showPills
                    pills={
                      <Pills
                        label="Garantia operacional"
                        state="default"
                        variant="light"
                        size="sm"
                      />
                    }
                  />
                </CardBalance>
              </div>
            </div>
            </div>

            {/* ── Seção Recebíveis ───────────────────────────────────── */}
            <div className="receivables-section">
              <div className="receivables-columns">

                {/* ── Coluna esquerda: gráfico ─────────────────────── */}
                <div className="receivables-chart-col">
                  <div className="receivables-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <div className="period-buttons">
                      <Button
                        variant={activePeriod === 'week' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('week')}
                      >
                        Semana
                      </Button>
                      <Button
                        variant={activePeriod === 'month' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('month')}
                      >
                        Mês
                      </Button>
                      <Button
                        variant={activePeriod === 'jan' ? 'fill' : 'outline'}
                        size="sm"
                        onClick={() => setActivePeriod('jan')}
                      >
                        janeiro/2026
                      </Button>
                    </div>
                  </div>

                  <div className="chart-container">
                    <div className="chart-bars">
                      {CHART_DATA.map((d) => {
                        const receivedPct = (d.received / MAX_CHART_VALUE) * 100;
                        const overduePct = (d.overdue / MAX_CHART_VALUE) * 100;
                        return (
                          <div key={d.label} className="chart-col">
                            <div className="bar-group">
                              <div
                                className="bar bar-received"
                                style={{ height: `${receivedPct}%` }}
                              />
                              <div
                                className="bar bar-overdue"
                                style={{ height: `${overduePct}%` }}
                              />
                            </div>
                            <Text
                              variant="caption-sm"
                              style={{
                                color: d.isToday
                                  ? 'var(--content-primary)'
                                  : 'var(--content-tertiary)',
                                textAlign: 'center',
                                fontWeight: d.isToday ? 600 : undefined,
                              }}
                            >
                              {d.isToday ? 'hoje' : d.label}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="chart-legend">
                    <div className="legend-item">
                      <span className="legend-dot legend-received" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Recebidos
                      </Text>
                    </div>
                    <div className="legend-item">
                      <span className="legend-dot legend-overdue" />
                      <Text variant="caption-sm" style={{ color: 'var(--content-secondary)' }}>
                        Em atraso
                      </Text>
                    </div>
                  </div>
                </div>

                {/* ── Coluna direita: lista de recebíveis ──────────── */}
                <div className="receivables-list-col">
                  <div className="receivables-list-header">
                    <Text variant="label-lg">Recebíveis</Text>
                    <Button variant="link" size="sm">ver todos</Button>
                  </div>
                  <div className="receivables-search-placeholder" />
                  <div className="receivables-list">
                    {RECEBIVEIS_MOCK.map((item) => (
                      <div key={item.id} className="receivable-item">
                        <Avatar variant="text" size="md" initials={item.initials} />
                        <div className="receivable-item-info">
                          <Text variant="label-sm">{item.name}</Text>
                          <Text variant="caption-sm" style={{ color: 'var(--content-tertiary)' }}>
                            {item.dueLabel}
                          </Text>
                        </div>
                        <div className="receivable-item-right">
                          <Text variant="label-sm">{item.amount}</Text>
                          <Text
                            variant="caption-sm"
                            style={{
                              color:
                                item.statusState === 'error'
                                  ? 'var(--accent-red)'
                                  : 'var(--accent-green)',
                            }}
                          >
                            {item.status}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </main>
    </SellerLayout>

    {/* ── Drawer: Solicitar saque ───────────────────────────────────────── */}
    <Drawer
      isOpen={drawerOpen}
      title={getSaqueDrawerTitle(saqueStep)}
      onClose={handleCloseDrawer}
      footer={getDrawerFooter()}
    >
      {renderDrawerContent()}
    </Drawer>

    {/* ── Drawer: Info de cada card (substitui os tooltips) ─────────────── */}
    <Drawer
      isOpen={infoCard !== null}
      title={infoCard ? CARD_INFO[infoCard].title : ''}
      onClose={closeInfoDrawer}
      footer={
        infoCard === 'futuros'
          ? {
              primaryButton: { children: 'Fechar', onClick: closeInfoDrawer },
              secondaryButton: {
                children: 'Solicitar saque',
                onClick: () => {
                  closeInfoDrawer();
                  handleOpenDrawer();
                },
              },
            }
          : {
              primaryButton: { children: 'Entendi', onClick: closeInfoDrawer },
            }
      }
    >
      {infoCard && (
        <div className="drawer-info-content">
          <Text
            variant="body-base"
            style={{ color: 'var(--content-secondary)', whiteSpace: 'pre-line' }}
          >
            {CARD_INFO[infoCard].intro}
          </Text>
          {CARD_INFO[infoCard].bullets && (
            <ul className="drawer-info-bullets">
              {CARD_INFO[infoCard].bullets!.map((b) => (
                <li key={b.term}>
                  <Text variant="body-base" style={{ color: 'var(--content-secondary)' }}>
                    <strong style={{ color: 'var(--content-primary)', fontWeight: 600 }}>
                      {b.term}:
                    </strong>{' '}
                    {b.description}
                  </Text>
                </li>
              ))}
            </ul>
          )}
          <Section title="">
            {CARD_INFO[infoCard].rows.map((row) => (
              <Section.Row
                key={row.label}
                label={row.label}
                content={
                  <Pills
                    label={row.value}
                    state={row.pillState}
                    variant="light"
                    size="sm"
                  />
                }
              />
            ))}
          </Section>
        </div>
      )}
    </Drawer>

    {/* ── Modal: "Conheça a Conta Corrente Barte" ──────────────────────────── */}
    <Modal
      isOpen={abrirContaModalOpen}
      title="Conheça a Conta Corrente Barte"
      onClose={() => setAbrirContaModalOpen(false)}
      size="sm"
      footer={{
        primaryButton: {
          children: 'Sim, quero conhecer',
          iconRight: <Icon icon={ArrowRightIcon} size={16} />,
          onClick: () => {
            setAbrirContaModalOpen(false);
            toast.success({
              title: 'Obrigado pelo interesse!',
              description:
                'Uma pessoa do nosso time vai entrar em contato com você em breve.',
            });
          },
        },
        secondaryButton: {
          children: 'Não tenho interesse',
          onClick: () => setAbrirContaModalOpen(false),
        },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Text variant="body-base">
          Se você tem interesse em conhecer a conta PJ Barte, nosso time de
          Banking entrará em contato para apresentar as funcionalidades,
          esclarecer dúvidas e explicar como a conta pode apoiar a gestão
          do seu saldo de vendas.
        </Text>
        <Text variant="body-base">
          Comece a movimentar seu saldo direto por aqui, é simples, prático
          e muito mais eficiente.
        </Text>
        <Text variant="body-base">Podemos entrar em contato com você?</Text>
      </div>
    </Modal>
    </>
  );
}
